import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Check, CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useHomepageText, getPlanI18nSlug } from '../../utils/homepageI18n';
import { normalizeUiLang } from '../../utils/localePreference';
import {
  formatPlanPrice,
  getDisplayCurrencyForLang,
  getUnifiedPlanPrice,
  isPaidUnifiedPlan,
  type PaidUnifiedPlanKey,
} from '../../config/unifiedPlans';
import { TEMPLATES_CHOOSE_URL } from './digitalCardRoutes';

export type DigitalCardPricingPlan = {
  name: string;
  price: string;
  description: string;
  features: string[];
  buttonText: string;
  buttonColor?: string;
  isPopular: boolean;
};

type Props = {
  plans: DigitalCardPricingPlan[];
  title?: string;
  subtitle?: string;
};

export const DigitalCardPricingSection: React.FC<Props> = ({ plans, title, subtitle }) => {
  const { t, i18n } = useTranslation();
  const { txt, txtList } = useHomepageText();
  const resolvedLang = normalizeUiLang(i18n.resolvedLanguage || i18n.language);
  const listingCurrency = getDisplayCurrencyForLang(resolvedLang);

  const localizedPlans = useMemo(
    () =>
      plans.map((plan) => {
        const slug = getPlanI18nSlug(plan.name || '');
        const prefix = slug ? `home_plan_${slug}` : '';
        const name = prefix ? txt(`${prefix}_name`, plan.name) : plan.name;
        const description = prefix ? txt(`${prefix}_desc`, plan.description) : plan.description;
        const buttonText = prefix ? txt(`${prefix}_button`, plan.buttonText) : plan.buttonText;
        const features = prefix ? txtList(`${prefix}_features`, plan.features) : plan.features;
        const planKey = slug && isPaidUnifiedPlan(slug) ? (slug as PaidUnifiedPlanKey) : null;
        const priceRaw = String(plan.price || '').trim();
        const isFree =
          slug === 'free' ||
          /^free$/i.test(priceRaw) ||
          priceRaw === '0' ||
          priceRaw === '$0';
        const localizedPrice =
          planKey
            ? formatPlanPrice(getUnifiedPlanPrice(planKey, 'monthly', listingCurrency), listingCurrency)
            : isFree && resolvedLang === 'sw'
              ? 'Bure'
              : isFree && resolvedLang === 'fr'
                ? 'Gratuit'
                : plan.price;
        return {
          ...plan,
          name,
          description,
          buttonText,
          features: Array.isArray(features) ? features : [],
          localizedPrice,
          showPerMonth: planKey != null || (!isFree && priceRaw !== 'Free'),
        };
      }),
    [plans, txt, txtList, resolvedLang, listingCurrency],
  );

  const sectionTitle = title || txt('home_pricing_title', 'Simple, Transparent Pricing');
  const sectionSubtitle =
    subtitle || txt('home_pricing_subtitle', 'Choose the plan that fits your professional needs');

  if (!localizedPlans.length) return null;

  return (
    <>
      <div className="dcl-section-header">
        <div className="dcl-section-badge">
          <CreditCard size={14} />
          {t('home_nav_pricing', { defaultValue: 'Pricing' })}
        </div>
        <h2 className="dcl-section-title dcl-section-title--center">{sectionTitle}</h2>
        <p className="dcl-section-sub dcl-section-sub--center" style={{ marginTop: 12 }}>
          {sectionSubtitle}
        </p>
      </div>

      <div className="dcl-pricing-grid">
        {localizedPlans.map((plan, index) => (
          <article
            key={`${plan.name}-${index}`}
            className={`dcl-pricing-card${plan.isPopular ? ' dcl-pricing-card--popular' : ''}`}
          >
            <div className="dcl-pricing-card-bar" aria-hidden="true" />
            {plan.isPopular ? (
              <span className="dcl-pricing-popular">{t('home_pricing_most_popular', { defaultValue: 'Most Popular' })}</span>
            ) : null}
            <div className="dcl-pricing-card-body">
              <h3 className="dcl-pricing-name">{plan.name}</h3>
              <div className="dcl-pricing-price-row">
                <span className="dcl-pricing-price">{plan.localizedPrice}</span>
                {plan.showPerMonth ? (
                  <span className="dcl-pricing-period">{t('home_pricing_per_month', { defaultValue: '/month' })}</span>
                ) : null}
              </div>
              <p className="dcl-pricing-desc">{plan.description}</p>
              {plan.features.length > 0 ? (
                <ul className="dcl-pricing-features">
                  {plan.features.map((feature) => (
                    <li key={feature}>
                      <span className="dcl-pricing-feature-icon" aria-hidden="true">
                        <Check size={12} />
                      </span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
              <Link
                to={TEMPLATES_CHOOSE_URL}
                className={`dcl-btn dcl-btn--pill dcl-btn--sm dcl-pricing-cta${
                  plan.isPopular ? ' dcl-btn--primary' : ' dcl-btn--secondary'
                }`}
              >
                {plan.buttonText || t('home_pricing_get_started', { defaultValue: 'Get Started' })}
              </Link>
            </div>
          </article>
        ))}
      </div>
    </>
  );
};

export default DigitalCardPricingSection;
